/**
 * Strategy Store
 * 
 * Zustand store for strategy management with cycle linking.
 * Part of Workstream 5: Strategy Chain
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { v4 as uuidv4 } from 'uuid';
import type {
  Strategy,
  StrategicDriver,
  StrategySummary,
  CreateStrategyInput,
  UpdateStrategyInput,
  CreateStrategicDriverInput,
  UpdateStrategicDriverInput,
  StrategyAlignmentCheck,
  StrategyFilters,
} from '../../types/prioritization/strategy';
import { normalizeDriverWeights } from '../../types/prioritization/strategy';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';

const TABLE_NAME = 'strategies';

interface StrategyState {
  // Data
  strategies: Record<string, Strategy>;
  
  // Loading state
  isLoading: boolean;
  error: string | null;
  
  // Supabase sync
  fetchFromSupabase: () => Promise<void>;
  
  // CRUD Actions
  createStrategy: (input: CreateStrategyInput) => Promise<{ ok: boolean; id?: string; error?: string }>;
  updateStrategy: (id: string, updates: UpdateStrategyInput) => void;
  deleteStrategy: (id: string) => Promise<{ ok: boolean; error?: string }>;
  activateStrategy: (id: string) => void;
  archiveStrategy: (id: string) => void;
  
  // Driver Actions
  addDriver: (input: CreateStrategicDriverInput) => StrategicDriver;
  addStrategicDriver: (strategyId: string, driver: Omit<CreateStrategicDriverInput, 'strategyId'>) => string;
  updateDriver: (strategyId: string, driverId: string, updates: UpdateStrategicDriverInput) => void;
  removeDriver: (strategyId: string, driverId: string) => void;
  reorderDrivers: (strategyId: string, driverIds: string[]) => void;
  normalizeWeights: (strategyId: string) => void;
  
  // Cycle Linking
  linkCyclesToStrategy: (strategyId: string, cycleIds: string[]) => void;
  linkCycle: (strategyId: string, cycleId: string) => void;
  unlinkCycleFromStrategy: (strategyId: string, cycleId: string) => void;
  
  // Criteria Linking
  linkCriteriaToDriver: (strategyId: string, driverId: string, criteriaId: string) => void;
  unlinkCriteriaFromDriver: (strategyId: string, driverId: string, criteriaId: string) => void;
  
  // Selectors
  getStrategy: (id: string) => Strategy | undefined;
  getAllStrategies: () => Strategy[];
  getStrategySummaries: () => StrategySummary[];
  getActiveStrategies: () => Strategy[];
  getStrategiesByFilter: (filters: StrategyFilters) => Strategy[];
  getStrategyForCycle: (cycleId: string) => Strategy | undefined;
  getStrategicDrivers: (strategyId: string) => StrategicDriver[];
  checkStrategyAlignment: (strategyId: string, cycleId: string, itemDriverMap: Record<string, string[]>) => StrategyAlignmentCheck;
  
  // Utility
  reset: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialState = {
  strategies: {},
  isLoading: false,
  error: null,
};

/**
 * Persist one strategy's embedded drivers to its row. Fire-and-forget like the
 * insert in createStrategy. Most driver mutators predate any UI and are still
 * local-only; the criteria-link mutators got UI (S3 driver alignment), and a
 * link that vanishes on reload is worse than no link.
 */
function persistStrategicDrivers(strategy: Strategy): void {
  if (!isSupabaseAvailable()) return;
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  supabase
    .from(TABLE_NAME)
    .update({
      strategic_drivers: strategy.strategicDrivers,
      updated_at: strategy.updatedAt.toISOString(),
    })
    .eq('id', strategy.id)
    .then(({ error }) => {
      if (error) console.error('[StrategyStore] Driver persist error:', error);
    });
}

export const useStrategyStore = create<StrategyState>()(
  (set, get) => ({
    ...initialState,
    
    // Supabase sync
    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) {
        console.log('[StrategyStore] Supabase not available');
        return;
      }
      
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) {
        set({ strategies: {}, isLoading: false, error: null });
        return;
      }
      
      set({ isLoading: true, error: null });
      
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        const { data, error } = await supabase
          .from(TABLE_NAME)
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('[StrategyStore] Fetch error:', error);
          set({ isLoading: false, error: error.message });
          return;
        }
        
        if (data) {
          const strategiesMap: Record<string, Strategy> = {};
          for (const row of data) {
            const strategy: Strategy = {
              id: row.id,
              name: row.name,
              description: row.description,
              strategicDrivers: row.strategic_drivers || [],
              timeHorizon: row.time_horizon,
              orgUnitId: row.org_unit_id,
              ownerId: row.owner_id,
              ownerName: row.owner_name,
              stakeholderIds: row.stakeholder_ids || [],
              status: row.status || 'draft',
              effectiveFrom: row.effective_from ? new Date(row.effective_from) : undefined,
              effectiveUntil: row.effective_until ? new Date(row.effective_until) : undefined,
              linkedCycleIds: row.linked_cycle_ids || [],
              createdAt: new Date(row.created_at),
              updatedAt: new Date(row.updated_at),
              workspaceId: row.workspace_id,
            };
            strategiesMap[strategy.id] = strategy;
          }
          set({ strategies: strategiesMap, isLoading: false, error: null });
          console.log(`[StrategyStore] Loaded ${data.length} strategies from Supabase (workspace: ${workspaceId})`);
        }
      } catch (err) {
        console.error('[StrategyStore] Fetch exception:', err);
        set({ isLoading: false, error: err instanceof Error ? err.message : 'Unknown error' });
      }
    },
    
    // Create strategy. Awaits the write so a failed insert never leaves a
    // strategy that only exists in this tab: the optimistic row is rolled
    // back and the caller gets a reason to show instead of a silent no-op.
    createStrategy: async (input: CreateStrategyInput) => {
        // Same up-front gate as createObjective (strategyAlignment.ts): the
        // server refuses a NULL workspace_id anyway (strategies_member_insert),
        // so say why before writing anything, instead of a raw RLS error.
        const workspaceId = input.workspaceId ?? getCurrentWorkspaceId();
        if (isSupabaseAvailable() && !workspaceId) {
          const message = 'Workspace required to create a strategy. Select a workspace and try again.';
          set({ error: message });
          return { ok: false, error: message };
        }
        const id = uuidv4();
        const now = new Date();

        const strategy: Strategy = {
          id,
          name: input.name,
          description: input.description,
          strategicDrivers: [],
          timeHorizon: input.timeHorizon,
          orgUnitId: input.orgUnitId,
          ownerId: input.ownerId,
          stakeholderIds: input.stakeholderIds || [],
          status: 'draft',
          effectiveFrom: input.effectiveFrom,
          effectiveUntil: input.effectiveUntil,
          linkedCycleIds: [],
          createdAt: now,
          updatedAt: now,
        };

        set(state => ({
          strategies: { ...state.strategies, [id]: strategy },
        }));

        // No Supabase configured (e.g. local/offline dev): keep the
        // optimistic row, same as before this fix.
        if (!isSupabaseAvailable()) {
          return { ok: true, id };
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          return { ok: true, id };
        }

        const dbRecord = {
          id: strategy.id,
          name: strategy.name,
          description: strategy.description,
          strategic_drivers: strategy.strategicDrivers,
          time_horizon: strategy.timeHorizon,
          org_unit_id: strategy.orgUnitId,
          owner_id: strategy.ownerId,
          stakeholder_ids: strategy.stakeholderIds,
          status: strategy.status,
          effective_from: strategy.effectiveFrom?.toISOString(),
          effective_until: strategy.effectiveUntil?.toISOString(),
          linked_cycle_ids: strategy.linkedCycleIds,
          created_at: strategy.createdAt.toISOString(),
          updated_at: strategy.updatedAt.toISOString(),
          workspace_id: workspaceId,
          // strategies_member_insert allows created_by NULL or the caller; the
          // creator is the signed-in owner — record it (the retired direct
          // writer in strategyAlignment.ts did).
          created_by: strategy.ownerId || null,
        };

        const { error } = await supabase.from(TABLE_NAME).insert(dbRecord);
        if (error) {
          console.error('[StrategyStore] Insert error:', error);
          set((state) => {
            const { [id]: _removed, ...rest } = state.strategies;
            return { strategies: rest, error: error.message };
          });
          return { ok: false, error: error.message };
        }

        return { ok: true, id };
      },
      
      // Update strategy
      updateStrategy: (id: string, updates: UpdateStrategyInput) => {
        set(state => {
          const existing = state.strategies[id];
          if (!existing) return state;
          
          const updated: Strategy = {
            ...existing,
            ...updates,
            updatedAt: new Date(),
          };
          
          return {
            strategies: { ...state.strategies, [id]: updated },
          };
        });
      },
      
      // Delete strategy (persist to Supabase so reloads do not resurrect rows)
      deleteStrategy: async (id: string) => {
        const prev = get().strategies[id];
        if (!prev) {
          return { ok: false, error: 'Strategy not found' };
        }

        set((state) => {
          const { [id]: _removed, ...rest } = state.strategies;
          return { strategies: rest, error: null };
        });

        if (!isSupabaseAvailable()) {
          return { ok: true };
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          return { ok: true };
        }

      const { data: deletedRows, error } = await supabase
          .from(TABLE_NAME)
          .delete()
          .eq('id', id)
          .select('id');

        if (error) {
          console.error('[StrategyStore] Delete error:', error);
          set((state) => ({
            strategies: { ...state.strategies, [id]: prev },
            error: error.message,
          }));
          return { ok: false, error: error.message };
        }

        if (!deletedRows?.length) {
          const msg =
            'Delete did not remove a row (permission or server mismatch). Try refreshing the page.';
          console.error('[StrategyStore] Delete removed 0 rows for strategy', id);
          set((state) => ({
            strategies: { ...state.strategies, [id]: prev },
            error: msg,
          }));
          return { ok: false, error: msg };
        }

        return { ok: true };
      },
      
      // Activate strategy
      activateStrategy: (id: string) => {
        get().updateStrategy(id, { status: 'active' });
      },
      
      // Archive strategy
      archiveStrategy: (id: string) => {
        get().updateStrategy(id, { status: 'archived' });
      },
      
      // Add driver
      addDriver: (input: CreateStrategicDriverInput): StrategicDriver => {
        const driverId = uuidv4();
        const strategy = get().strategies[input.strategyId];
        
        if (!strategy) {
          throw new Error(`Strategy ${input.strategyId} not found`);
        }
        
        const driver: StrategicDriver = {
          id: driverId,
          name: input.name,
          description: input.description,
          weight: input.weight,
          linkedCriteriaIds: input.linkedCriteriaIds || [],
          ownerId: input.ownerId,
          order: strategy.strategicDrivers.length,
          isActive: true,
        };
        
        set(state => {
          const existing = state.strategies[input.strategyId];
          if (!existing) return state;
          
          return {
            strategies: {
              ...state.strategies,
              [input.strategyId]: {
                ...existing,
                strategicDrivers: [...existing.strategicDrivers, driver],
                updatedAt: new Date(),
              },
            },
          };
        });
        
        return driver;
      },
      
      // Add strategic driver (convenience: strategyId + driver payload, returns driver id)
      addStrategicDriver: (strategyId: string, driver: Omit<CreateStrategicDriverInput, 'strategyId'>): string => {
        const d = get().addDriver({ strategyId, ...driver });
        return d.id;
      },
      
      // Update driver
      updateDriver: (strategyId: string, driverId: string, updates: UpdateStrategicDriverInput) => {
        set(state => {
          const strategy = state.strategies[strategyId];
          if (!strategy) return state;
          
          const driverIndex = strategy.strategicDrivers.findIndex(d => d.id === driverId);
          if (driverIndex < 0) return state;
          
          const updatedDrivers = [...strategy.strategicDrivers];
          updatedDrivers[driverIndex] = { ...updatedDrivers[driverIndex], ...updates };
          
          return {
            strategies: {
              ...state.strategies,
              [strategyId]: {
                ...strategy,
                strategicDrivers: updatedDrivers,
                updatedAt: new Date(),
              },
            },
          };
        });
      },
      
      // Remove driver
      removeDriver: (strategyId: string, driverId: string) => {
        set(state => {
          const strategy = state.strategies[strategyId];
          if (!strategy) return state;
          
          return {
            strategies: {
              ...state.strategies,
              [strategyId]: {
                ...strategy,
                strategicDrivers: strategy.strategicDrivers.filter(d => d.id !== driverId),
                updatedAt: new Date(),
              },
            },
          };
        });
      },
      
      // Reorder drivers
      reorderDrivers: (strategyId: string, driverIds: string[]) => {
        set(state => {
          const strategy = state.strategies[strategyId];
          if (!strategy) return state;
          
          const reorderedDrivers = driverIds
            .map((id, index) => {
              const driver = strategy.strategicDrivers.find(d => d.id === id);
              return driver ? { ...driver, order: index } : null;
            })
            .filter((d): d is StrategicDriver => d !== null);
          
          return {
            strategies: {
              ...state.strategies,
              [strategyId]: {
                ...strategy,
                strategicDrivers: reorderedDrivers,
                updatedAt: new Date(),
              },
            },
          };
        });
      },
      
      // Normalize weights
      normalizeWeights: (strategyId: string) => {
        set(state => {
          const strategy = state.strategies[strategyId];
          if (!strategy) return state;
          
          const normalizedDrivers = normalizeDriverWeights(strategy.strategicDrivers);
          
          return {
            strategies: {
              ...state.strategies,
              [strategyId]: {
                ...strategy,
                strategicDrivers: normalizedDrivers,
                updatedAt: new Date(),
              },
            },
          };
        });
      },
      
      // Link single cycle (convenience for integration/test contract)
      linkCycle: (strategyId: string, cycleId: string) => {
        get().linkCyclesToStrategy(strategyId, [cycleId]);
      },
      
      // Link cycles
      linkCyclesToStrategy: (strategyId: string, cycleIds: string[]) => {
        set(state => {
          const strategy = state.strategies[strategyId];
          if (!strategy) return state;
          
          const newCycleIds = [...new Set([...strategy.linkedCycleIds, ...cycleIds])];
          
          return {
            strategies: {
              ...state.strategies,
              [strategyId]: {
                ...strategy,
                linkedCycleIds: newCycleIds,
                updatedAt: new Date(),
              },
            },
          };
        });
      },
      
      // Unlink cycle
      unlinkCycleFromStrategy: (strategyId: string, cycleId: string) => {
        set(state => {
          const strategy = state.strategies[strategyId];
          if (!strategy) return state;
          
          return {
            strategies: {
              ...state.strategies,
              [strategyId]: {
                ...strategy,
                linkedCycleIds: strategy.linkedCycleIds.filter(id => id !== cycleId),
                updatedAt: new Date(),
              },
            },
          };
        });
      },
      
      // Link criteria to driver
      linkCriteriaToDriver: (strategyId: string, driverId: string, criteriaId: string) => {
        set(state => {
          const strategy = state.strategies[strategyId];
          if (!strategy) return state;

          const driverIndex = strategy.strategicDrivers.findIndex(d => d.id === driverId);
          if (driverIndex < 0) return state;

          const driver = strategy.strategicDrivers[driverIndex];
          if (driver.linkedCriteriaIds.includes(criteriaId)) return state;

          const updatedDrivers = [...strategy.strategicDrivers];
          updatedDrivers[driverIndex] = {
            ...driver,
            linkedCriteriaIds: [...driver.linkedCriteriaIds, criteriaId],
          };

          return {
            strategies: {
              ...state.strategies,
              [strategyId]: {
                ...strategy,
                strategicDrivers: updatedDrivers,
                updatedAt: new Date(),
              },
            },
          };
        });
        const updated = get().strategies[strategyId];
        if (updated) persistStrategicDrivers(updated);
      },

      // Unlink criteria from driver
      unlinkCriteriaFromDriver: (strategyId: string, driverId: string, criteriaId: string) => {
        set(state => {
          const strategy = state.strategies[strategyId];
          if (!strategy) return state;

          const driverIndex = strategy.strategicDrivers.findIndex(d => d.id === driverId);
          if (driverIndex < 0) return state;

          const driver = strategy.strategicDrivers[driverIndex];
          const updatedDrivers = [...strategy.strategicDrivers];
          updatedDrivers[driverIndex] = {
            ...driver,
            linkedCriteriaIds: driver.linkedCriteriaIds.filter(id => id !== criteriaId),
          };

          return {
            strategies: {
              ...state.strategies,
              [strategyId]: {
                ...strategy,
                strategicDrivers: updatedDrivers,
                updatedAt: new Date(),
              },
            },
          };
        });
        const updated = get().strategies[strategyId];
        if (updated) persistStrategicDrivers(updated);
      },
      
      // Get strategy
      getStrategy: (id: string): Strategy | undefined => {
        return get().strategies[id];
      },
      
      // Get all strategies
      getAllStrategies: (): Strategy[] => {
        return Object.values(get().strategies);
      },
      
      // Get strategy summaries
      getStrategySummaries: (): StrategySummary[] => {
        return Object.values(get().strategies).map(s => ({
          id: s.id,
          name: s.name,
          status: s.status,
          timeHorizon: s.timeHorizon,
          driverCount: s.strategicDrivers.length,
          linkedCycleCount: s.linkedCycleIds.length,
          ownerName: s.ownerName,
          effectiveFrom: s.effectiveFrom,
          effectiveUntil: s.effectiveUntil,
        }));
      },
      
      // Get active strategies
      getActiveStrategies: (): Strategy[] => {
        return Object.values(get().strategies).filter(s => s.status === 'active');
      },
      
      // Get by filter
      getStrategiesByFilter: (filters: StrategyFilters): Strategy[] => {
        let results = Object.values(get().strategies);
        
        if (filters.status && filters.status.length > 0) {
          results = results.filter(s => filters.status!.includes(s.status));
        }
        
        if (filters.timeHorizon && filters.timeHorizon.length > 0) {
          results = results.filter(s => filters.timeHorizon!.includes(s.timeHorizon));
        }
        
        if (filters.orgUnitId) {
          results = results.filter(s => s.orgUnitId === filters.orgUnitId);
        }
        
        if (filters.ownerId) {
          results = results.filter(s => s.ownerId === filters.ownerId);
        }
        
        if (filters.effectiveAsOf) {
          results = results.filter(s => {
            if (s.effectiveFrom && s.effectiveFrom > filters.effectiveAsOf!) return false;
            if (s.effectiveUntil && s.effectiveUntil < filters.effectiveAsOf!) return false;
            return true;
          });
        }
        
        return results;
      },
      
      // Get strategy for cycle
      getStrategyForCycle: (cycleId: string): Strategy | undefined => {
        return Object.values(get().strategies).find(s => 
          s.linkedCycleIds.includes(cycleId)
        );
      },
      
      // Get strategic drivers
      getStrategicDrivers: (strategyId: string): StrategicDriver[] => {
        const strategy = get().strategies[strategyId];
        return strategy?.strategicDrivers || [];
      },
      
      // Check strategy alignment
      checkStrategyAlignment: (strategyId: string, cycleId: string, itemDriverMap: Record<string, string[]>): StrategyAlignmentCheck => {
        const strategy = get().strategies[strategyId];
        
        if (!strategy) {
          return {
            strategyId,
            cycleId,
            alignmentScore: 0,
            driverCoverage: [],
            uncoveredDrivers: [],
            itemsNotAligned: [],
            recommendations: ['Strategy not found'],
          };
        }
        
        const itemIds = Object.keys(itemDriverMap);
        const driverCoverage: StrategyAlignmentCheck['driverCoverage'] = [];
        const uncoveredDrivers: string[] = [];
        const itemsNotAligned: Array<{ id: string; title: string }> = [];
        
        // Check each driver
        for (const driver of strategy.strategicDrivers) {
          const linkedItems = itemIds.filter(itemId => 
            itemDriverMap[itemId]?.includes(driver.id)
          );
          
          driverCoverage.push({
            driverId: driver.id,
            driverName: driver.name,
            itemCount: linkedItems.length,
            itemPercentage: (linkedItems.length / Math.max(itemIds.length, 1)) * 100,
            topItems: linkedItems.slice(0, 3).map(id => ({ id, title: '' })),
          });
          
          if (linkedItems.length === 0) {
            uncoveredDrivers.push(driver.id);
          }
        }
        
        // Find items not aligned to any driver
        for (const itemId of itemIds) {
          const drivers = itemDriverMap[itemId] || [];
          if (drivers.length === 0) {
            itemsNotAligned.push({ id: itemId, title: '' });
          }
        }
        
        // Calculate alignment score
        const coveredDrivers = strategy.strategicDrivers.length - uncoveredDrivers.length;
        const alignedItems = itemIds.length - itemsNotAligned.length;
        const alignmentScore = Math.round(
          ((coveredDrivers / Math.max(strategy.strategicDrivers.length, 1)) * 50) +
          ((alignedItems / Math.max(itemIds.length, 1)) * 50)
        );
        
        // Generate recommendations
        const recommendations: string[] = [];
        if (uncoveredDrivers.length > 0) {
          recommendations.push(`${uncoveredDrivers.length} strategic driver(s) have no aligned items`);
        }
        if (itemsNotAligned.length > 0) {
          recommendations.push(`${itemsNotAligned.length} item(s) are not aligned to any strategic driver`);
        }
        
        return {
          strategyId,
          cycleId,
          alignmentScore,
          driverCoverage,
          uncoveredDrivers,
          itemsNotAligned,
          recommendations,
        };
      },
      
      // Reset
      reset: () => set(initialState),
      
      // Loading
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
    })
);

// Selector hooks
export const useStrategy = (id: string) =>
  useStrategyStore(state => state.getStrategy(id));

export const useAllStrategies = () =>
  useStrategyStore(useShallow((state) => Object.values(state.strategies)));

export const useActiveStrategies = () =>
  useStrategyStore(
    useShallow((state) =>
      Object.values(state.strategies).filter((s) => s.status === 'active'),
    ),
  );

export const useStrategicDrivers = (strategyId: string) =>
  useStrategyStore(state => state.getStrategicDrivers(strategyId));

export const useStrategyForCycle = (cycleId: string) =>
  useStrategyStore(state => state.getStrategyForCycle(cycleId));
